import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Call Us',
      primary: '9019206699',
      secondary: 'Regional Bangalore Line',
      href: 'tel:9019206699'
    },
    {
      icon: MapPin,
      title: 'Location',
      primary: '#330, Whitecity Layout, Bangalore-49',
      secondary: 'Opp. to Blossom International School',
      href: 'https://maps.google.com/?q=Whitecity+Layout+Bangalore'
    },
    {
      icon: Mail,
      title: 'Email Support',
      primary: 'info@adigital.tv',
      secondary: 'support@adigital.tv',
      href: 'mailto:info@adigital.tv'
    },
    {
      icon: Clock,
      title: 'Support Hours',
      primary: '24/7 Ticketing System',
      secondary: 'Quick response guaranteed',
      href: null
    }
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="bg-white py-12 md:py-16 border-b border-gray-100">
        <div className="container-custom">
          <span className="badge badge-featured mb-4">Get in Touch</span>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contact Us
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            Have questions about our services? We're here to help. Reach out via phone, 
            email, or fill out the form below.
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-12 bg-[#F9FAFB]">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactInfo.map((info, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg hover:border-[#D92323]/20 transition-all"
                data-testid={`contact-card-${index}`}
              >
                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                  <info.icon size={24} className="text-[#D92323]" strokeWidth={2} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{info.title}</h3>
                {info.href ? (
                  <a 
                    href={info.href}
                    className="block text-gray-700 hover:text-[#D92323] transition-colors font-medium"
                    target={info.href.startsWith('http') ? '_blank' : undefined}
                    rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {info.primary}
                  </a>
                ) : (
                  <p className="text-gray-700 font-medium">{info.primary}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">{info.secondary}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form and Map Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Send us a Message
              </h2>
              
              {isSubmitted ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center" data-testid="success-message">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" strokeWidth={2} />
                  </div>
                  <h3 className="text-xl font-bold text-green-800 mb-2">Message Sent!</h3>
                  <p className="text-green-700 mb-4">
                    Thank you for contacting us. Our team will get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-green-700 font-semibold hover:text-green-800 underline"
                    data-testid="send-another"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323] transition-colors"
                      placeholder="Your full name"
                      data-testid="input-name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323] transition-colors"
                        placeholder="your@email.com"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323] transition-colors"
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323] transition-colors resize-none"
                      placeholder="Tell us about your requirements..."
                      data-testid="input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} strokeWidth={2} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Company Info */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Visit Our Office
              </h2>
              
              <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  Assistive Netspeed Technologies Private Limited
                </h3>
                <p className="text-gray-600 mb-4">
                  Your trusted partner for FTTH and IPTV solutions in Bangalore and across India.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin size={20} className="text-[#D92323] mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <div>
                      <p className="text-gray-900 font-medium">#330, Whitecity Layout</p>
                      <p className="text-gray-600">Bangalore-49</p>
                      <p className="text-sm text-gray-500">Opp. to Blossom International School</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={20} className="text-[#D92323] flex-shrink-0" strokeWidth={2} />
                    <a href="tel:9019206699" className="text-gray-900 font-medium hover:text-[#D92323]">
                      9019206699
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={20} className="text-[#D92323] flex-shrink-0" strokeWidth={2} />
                    <a href="mailto:info@adigital.tv" className="text-gray-900 font-medium hover:text-[#D92323]">
                      info@adigital.tv
                    </a>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="bg-gray-100 rounded-2xl h-64 flex items-center justify-center overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.988661891428!2d77.5945627!3d12.9165758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae15c7b79f42d9%3A0x4c5d6f7e8b9a0c1d!2sBangalore%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1234567890123"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                  className="rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact CTA */}
      <section className="py-12 bg-[#0A0A0A] text-white">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold mb-2">Prefer to Talk?</h3>
              <p className="text-gray-400">Our customer service team is available to help you.</p>
            </div>
            <a 
              href="tel:9019206699" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="quick-call"
            >
              <Phone size={18} strokeWidth={2} />
              Call 9019206699
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
